/**
 * FileDragDropHandler - TypeScript implementation for file drag and drop
 *
 * Based on best practices from:
 * - Smashing Magazine's vanilla JS implementation
 * - Angular directive patterns from GitHub gist by darrenmothersele
 * - MDN Web APIs documentation for HTML Drag and Drop API
 *
 * Features:
 * - TypeScript typed interfaces
 * - Event delegation pattern
 * - Visual feedback during drag operations
 * - File validation
 * - Multiple file support
 * - Error handling
 */
import { validateFiles } from "./utils.js";
// file-drag-drop-handler.ts
export class FileDragDropHandler {
    element;
    options;
    events;
    dragCounter = 0;
    isDragActive = false;
    constructor(element, events = {}) {
        this.element = element;
        // Set default options
        this.options = element.options;
        this.events = events;
        this.init();
    }
    init() {
        this.setupEventListeners();
        if (this.options.preventBodyDrop) {
            this.preventBodyDrop();
        }
    }
    setupEventListeners() {
        // Prevent default browser behavior for drag events
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            this.element.addEventListener(eventName, this.preventDefaults.bind(this), false);
        });
        // Handle drag enter/over for visual feedback
        ['dragenter', 'dragover'].forEach(eventName => {
            this.element.addEventListener(eventName, this.handleDragEnterOver.bind(this), false);
        });
        // Handle drag leave/drop to remove visual feedback
        ['dragleave', 'drop'].forEach(eventName => {
            this.element.addEventListener(eventName, this.handleDragLeaveEnd.bind(this), false);
        });
        // Handle the actual file drop
        this.element.addEventListener('drop', this.handleFileDrop.bind(this), false);
    }
    preventBodyDrop() {
        // Prevent default drag behaviors on body
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            document.body.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
            }, false);
        });
    }
    preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }
    handleDragEnterOver(e) {
        this.dragCounter++;
        if (!this.isDragActive) {
            this.isDragActive = true;
            this.element.classList.add(this.options.dragActiveClass);
        }
        // Check if dragged items contain files
        if (this.containsFiles(e)) {
            this.element.classList.add(this.options.dragOverClass);
            this.events.onDragOver?.(e);
        }
        this.events.onDragEnter?.(e);
    }
    handleDragLeaveEnd(e) {
        this.dragCounter--;
        if (this.dragCounter === 0) {
            this.isDragActive = false;
            this.element.classList.remove(this.options.dragActiveClass);
            this.element.classList.remove(this.options.dragOverClass);
            this.events.onDragLeave?.(e);
        }
    }
    containsFiles(e) {
        if (!e.dataTransfer?.types)
            return false;
        return Array.from(e.dataTransfer.types).includes('Files');
    }
    handleFileDrop(e) {
        this.dragCounter = 0;
        this.isDragActive = false;
        this.element.classList.remove(this.options.dragActiveClass);
        this.element.classList.remove(this.options.dragOverClass);
        const files = this.extractFiles(e);
        if (files.length === 0) {
            this.events.onError?.('No files detected in drop event');
            return;
        }
        const validation = this.validateFiles(files);
        if (!validation.valid) {
            this.events.onError?.(validation.errors.join(', '), validation.invalidFiles);
            // Still process valid files if any
            if (validation.validFiles.length > 0) {
                this.events.onFileDrop?.(validation.validFiles);
            }
            return;
        }
        this.events.onFileDrop?.(validation.validFiles);
    }
    extractFiles(e) {
        const files = [];
        if (e.dataTransfer?.items) {
            // Use DataTransferItemList interface
            for (let i = 0; i < e.dataTransfer.items.length; i++) {
                const item = e.dataTransfer.items[i];
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) {
                        files.push(file);
                    }
                }
            }
            e.dataTransfer.items.clear();
        }
        else if (e.dataTransfer?.files) {
            // Fallback to FileList
            files.push(...Array.from(e.dataTransfer.files));
            e.dataTransfer.clearData();
        }
        return files;
    }
    validateFiles(files) {
        return validateFiles(files, this.options);
    }
    /**
     * Destroy the drag and drop handler
     */
    destroy() {
        // Remove all event listeners
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            this.element.removeEventListener(eventName, this.preventDefaults.bind(this), false);
        });
        ['dragenter', 'dragover'].forEach(eventName => {
            this.element.removeEventListener(eventName, this.handleDragEnterOver.bind(this), false);
        });
        ['dragleave', 'drop'].forEach(eventName => {
            this.element.removeEventListener(eventName, this.handleDragLeaveEnd.bind(this), false);
        });
        this.element.removeEventListener('drop', this.handleFileDrop.bind(this), false);
        // Clean up classes
        this.element.classList.remove(this.options.dragActiveClass);
        this.element.classList.remove(this.options.dragOverClass);
    }
    /**
     * Check if drag and drop is supported
     */
    static isSupported() {
        return 'ondragstart' in document.createElement('div') &&
            'ondrop' in document.createElement('div') &&
            'FileReader' in window &&
            'File' in window;
    }
}
/**
 * Utility function to create a drag and drop zone
 */
// export function createDragDropZone(
//     element: HTMLElement | string,
//     options: DragDropOptions = {},
//     events: DragDropEvents = {}
// ): FileDragDropHandler {
//     return new FileDragDropHandler(element, options, events);
// }
/**
 * Example usage:
 *
 * const dropZone = createDragDropZone('#drop-area', {
 *     maxFileSize: 5 * 1024 * 1024, // 5MB
 *     allowedTypes: ['image/jpeg', 'image/png', 'image/gif'],
 *     maxFiles: 5,
 *     multiple: true
 * }, {
 *     onFileDrop: (files) => {
 *         console.log('Files dropped:', files);
 *     },
 *     onError: (error) => {
 *         console.error('Drag drop error:', error);
 *     }
 * });
 */ 
